import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';
import { t } from '../lib/theme';
import MonthlyOncall from './MonthlyOncall';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const getJordanDate = () => new Date(new Date().getTime() + 3*60*60*1000);
const getTodayDay   = () => DAYS[getJordanDate().getDay()];
const getTodayDateStr = () => {
  const d = getJordanDate();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
};
const getWeekRange = () => {
  const j = getJordanDate();
  const dow = j.getUTCDay();
  const sun = new Date(j.getTime() - dow*86400000);
  const sat = new Date(sun.getTime() + 6*86400000);
  const fmt = d => `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
  return { start: fmt(sun), end: fmt(sat) };
};
const getDailyInspiration = (quotes) => {
  if (!quotes.length) return null;
  const j = getJordanDate();
  const start = new Date(Date.UTC(j.getUTCFullYear(),0,0));
  const doy = Math.floor((j-start)/86400000);
  return quotes[doy % quotes.length];
};
const formatDate = (d) => d ? new Date(d+'T00:00:00').toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'}) : '';

export default function HomePage({ isMobile, isDirector, onEditHome }) {
  const [news, setNews]             = useState([]);
  const [oncall, setOncall]         = useState([]);
  const [activity, setActivity]     = useState(null);
  const [inspiration, setInspiration] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showMonthly, setShowMonthly] = useState(false);
  const today = getTodayDay();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const { data: content } = await supabase.from('homepage_content').select('*').eq('is_active',true).order('display_order');
    const { start, end } = getWeekRange();
    const { data: weekOncall } = await supabase.from('monthly_oncall').select('*').gte('oncall_date',start).lte('oncall_date',end).order('oncall_date');

    if (content) {
      setNews(content.filter(d=>d.content_type==='news').slice(0,3));
      setActivity(content.filter(d=>d.content_type==='next_activity')[0]||null);
      setInspiration(getDailyInspiration(content.filter(d=>d.content_type==='inspiration')));
    }
    if (weekOncall) {
      setOncall(weekOncall.map(e => {
        const [y,m,d] = e.oncall_date.split('-').map(Number);
        return { day_of_week: DAYS[new Date(Date.UTC(y,m-1,d,12,0,0)).getUTCDay()], fellow_name: e.fellow_name };
      }));
    }
    setLoading(false);
  };

  if (loading) return (
    <div style={{ padding:60, textAlign:'center', color:t.textL, fontFamily:t.fonts.body }}>
      <div style={{ fontSize:28, marginBottom:10, color:t.clay }}>✦</div>Loading…
    </div>
  );

  return (
    <div style={{ background:t.sand, minHeight:'100vh', paddingBottom:isMobile?80:0, fontFamily:t.fonts.body }}>

      {/* Hero */}
      <div style={{ position:'relative', height:isMobile?145:190, overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:"url('https://images.unsplash.com/photo-1548199569-4fcc6b1c0df6?w=1200&q=85')", backgroundSize:'cover', backgroundPosition:'center 55%' }}/>
        <div style={{ position:'absolute', inset:0, background:`linear-gradient(to bottom, rgba(61,79,92,0.28) 0%, rgba(245,239,230,1) 100%)` }}/>
        <div style={{ position:'relative', zIndex:2, height:'100%', display:'flex', flexDirection:'column', justifyContent:'flex-end', padding:isMobile?'0 16px 16px':'0 28px 18px' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(255,255,255,0.92)', borderRadius:20, padding:'3px 12px', marginBottom:10, width:'fit-content', fontSize:10, color:t.slate, fontWeight:600, letterSpacing:'0.5px', textTransform:'uppercase' }}>
            ✦ HPM Fellowship @ KHCC
          </div>
          <div style={{ fontFamily:t.fonts.display, fontSize:isMobile?19:25, fontWeight:400, color:t.text, lineHeight:1.25 }}>
            Welcome to the official Hospice and{' '}
            <em style={{ color:t.clay, fontStyle:'italic' }}>Palliative</em>{' '}
            Medicine Fellowship at KHCC
          </div>
        </div>
      </div>

      <div style={{ padding:isMobile?'14px 14px 0':'18px 24px 0', maxWidth:1100, margin:'0 auto' }}>
        {isDirector && (
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
            <button onClick={onEditHome} style={{ padding:'8px 16px', background:t.teal, color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:t.fonts.body }}>
              ✎ Edit Home Page
            </button>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr', gap:14 }}>

          {/* News */}
          <div style={cs.card}>
            <div style={cs.cardHdr}><div style={cs.cardTitle}>📢 Department News</div></div>
            {news.length===0
              ? <div style={cs.empty}>No announcements yet.</div>
              : news.map(n => (
                <div key={n.id} style={cs.newsItem}>
                  <div style={cs.newsDate}>{new Date(n.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</div>
                  <div style={cs.newsText}>{n.body}</div>
                  {n.author && <div style={cs.newsBy}>📌 {n.author}</div>}
                </div>
              ))}
          </div>

          {/* On-call + Activity */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={cs.card}>
              <div style={cs.cardHdr}>
                <div style={cs.cardTitle}>📅 On-Call This Week</div>
                <button onClick={() => setShowMonthly(true)} style={cs.cardLink}>Monthly →</button>
              </div>
              {DAYS.map(day => {
                const entry   = oncall.find(o=>o.day_of_week===day);
                const isToday = day===today;
                const isWeekend = day==='Fri'||day==='Sat';
                return (
                  <div key={day} style={{ ...cs.ocRow, ...(isToday?cs.ocToday:{}) }}>
                    <span style={{ ...cs.ocDay, ...(isToday?{color:t.clay,fontWeight:800}:isWeekend?{color:t.clay}:{}) }}>{day}</span>
                    <span style={cs.ocName}>{entry?.fellow_name||'TBD'}</span>
                    {isToday && <span style={cs.todayBadge}>Today</span>}
                  </div>
                );
              })}
            </div>
            {activity && (
              <div style={cs.card}>
                <div style={cs.cardHdr}><div style={cs.cardTitle}>🗓 Next Activity</div></div>
                <div style={cs.actBox}>
                  <div style={cs.actLabel}>Coming up</div>
                  <div style={cs.actName}>{activity.title}</div>
                  {activity.event_date && <div style={cs.actRow}>📅 {formatDate(activity.event_date)}</div>}
                  {activity.event_time && <div style={cs.actRow}>🕒 {activity.event_time}</div>}
                  {activity.event_location && <div style={cs.actRow}>📍 {activity.event_location}</div>}
                  {activity.event_topic && <div style={{ ...cs.actRow, fontWeight:600, color:t.teal, marginTop:4 }}>{activity.event_topic}</div>}
                </div>
              </div>
            )}
          </div>

          {/* Inspiration */}
          {inspiration && (
            <div style={cs.card}>
              <div style={cs.cardHdr}><div style={cs.cardTitle}>✨ Inspiration of the Day</div></div>
              <div style={cs.inspoDay}>{getJordanDate().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'})}</div>
              <div style={cs.inspoQ}>"{inspiration.body}"</div>
              <div style={cs.inspoA}>— {inspiration.title}</div>
            </div>
          )}
        </div>
      </div>

      {showMonthly && <MonthlyOncall isMobile={isMobile} onClose={() => setShowMonthly(false)} />}
    </div>
  );
}

const cs = {
  card:       { background:'#fff', borderRadius:13, padding:'15px 17px', boxShadow:'0 2px 16px rgba(60,50,40,0.10)', border:'1px solid rgba(200,149,108,0.18)' },
  cardHdr:    { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:11 },
  cardTitle:  { fontSize:13, fontWeight:600, color:t.slate, display:'flex', alignItems:'center', gap:7 },
  cardLink:   { fontSize:11, color:t.teal, background:'none', border:'none', cursor:'pointer', fontWeight:600, fontFamily:t.fonts.body, padding:0 },
  empty:      { fontSize:13, color:'#aaa', fontStyle:'italic', textAlign:'center', padding:'12px 0' },
  newsItem:   { padding:'8px 10px', borderRadius:8, borderLeft:`3px solid ${t.teal}`, background:t.tealBg, marginBottom:7 },
  newsDate:   { fontSize:9, color:t.teal, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 },
  newsText:   { fontSize:12, color:t.text, lineHeight:1.5 },
  newsBy:     { fontSize:10, color:t.textL, marginTop:4 },
  ocRow:      { display:'flex', alignItems:'center', padding:'5px 7px', borderRadius:6, marginBottom:3 },
  ocToday:    { background:'rgba(200,149,108,0.10)', border:'1px solid rgba(200,149,108,0.2)' },
  ocDay:      { fontSize:11, fontWeight:700, color:t.slate, width:32 },
  ocName:     { fontSize:11, color:t.textL, flex:1 },
  todayBadge: { fontSize:9, fontWeight:700, color:t.clay, background:'rgba(200,149,108,0.14)', padding:'2px 7px', borderRadius:20 },
  actBox:     { background:t.tealBg, border:`1px solid rgba(46,125,107,0.15)`, borderRadius:8, padding:'11px 13px' },
  actLabel:   { fontSize:9, fontWeight:700, color:t.teal, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:4 },
  actName:    { fontFamily:t.fonts.display, fontSize:17, fontWeight:600, color:t.slate, marginBottom:4 },
  actRow:     { fontSize:11, color:t.textL },
  inspoDay:   { fontSize:9, fontWeight:700, color:t.clay, background:'rgba(200,149,108,0.12)', padding:'2px 9px', borderRadius:20, marginBottom:9, display:'inline-block', textTransform:'uppercase', letterSpacing:'0.5px' },
  inspoQ:     { fontFamily:t.fonts.display, fontSize:14, fontStyle:'italic', color:t.slate, lineHeight:1.65, padding:'10px 12px', borderLeft:`3px solid ${t.clay}`, background:'rgba(200,149,108,0.06)', borderRadius:'0 8px 8px 0' },
  inspoA:     { fontSize:10, color:t.textL, marginTop:8, textAlign:'right' },
};
